import { InterpolationPrimitive } from '@emotion/serialize'
import { Icon } from 'components/icon'
import { usePopperModal } from 'components/modal/PopperModal'
import {
  SWizardContent,
  SWizardContentHeading,
  SWizardContentHeadingTitle,
  SWizardContentHeadingTooltip,
  SWizardContentHeadingTooltipWrapper,
} from './WizardContent.styled'
import { WizardStepHeader } from 'components/wizard/wizardStepHeader/WizardStepHeader'
import { WizardSubtitle } from 'components/wizard/WizardTypography'
import { forwardRef, HTMLAttributes, ReactNode } from 'react'
import { colors } from 'utils/colors'
import { useWindowSize } from 'utils/hooks'

export function WizardContentHeading(props: {
  title: ReactNode
  action?: ReactNode
  tooltip?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <SWizardContentHeading className={props.className}>
      <SWizardContentHeadingTitle>
        <WizardSubtitle css={{ marginBottom: '0 !important;' }}>{props.title}</WizardSubtitle>
        {!!props.tooltip && (
          <WizardContentHeadingTooltip>{props.tooltip}</WizardContentHeadingTooltip>
        )}
        {props.action}
      </SWizardContentHeadingTitle>
      {props.children}
    </SWizardContentHeading>
  )
}

export function WizardContentHeadingTooltip(props: { children?: ReactNode }) {
  const { isDesktop } = useWindowSize()
  const { modal, setReferenceElement, open, close } = usePopperModal(
    () => (
      <SWizardContentHeadingTooltip orientation="right">
        {props.children}
      </SWizardContentHeadingTooltip>
    ),
    { placement: 'top', noStyle: true }
  )

  return (
    <SWizardContentHeadingTooltipWrapper
      ref={setReferenceElement}
      onClick={open}
      // if we don't disable onMouseEnter on mobile, open function triggers twice on mobile
      // which causes modal to stay hidden for first press on mobile
      onMouseEnter={isDesktop ? open : undefined}
      onMouseLeave={isDesktop ? close : undefined}
    >
      <Icon name="Question" color={colors.gray[500]} size="1.25rem" css={{ cursor: 'pointer' }} />
      {modal}
    </SWizardContentHeadingTooltipWrapper>
  )
}

export const WizardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    className?: string
    hideBack?: boolean
    onBackClick?: () => void
    actions?: ReactNode
    mainCss?: InterpolationPrimitive
    headerCss?: InterpolationPrimitive
    steps?: { current: number; total: number }
  }
  // eslint-disable-next-line unused-imports/no-unused-vars
>(({ hideBack, actions, onBackClick, children, mainCss, headerCss, ...props }, ref) => {
  return (
    <SWizardContent ref={ref} {...props}>
      <WizardStepHeader
        css={headerCss}
        onBackClick={onBackClick}
        hideBack={hideBack}
        {...props.steps}
      />

      <main css={mainCss}>{children}</main>
    </SWizardContent>
  )
})
WizardContent.displayName = 'WizardContent'
